import ReactDOMServer from "react-dom/server"
import React, { ChangeEvent, MutableRefObject } from 'react'
import { getLocale, Locale } from '../src/model/locale'
import { FencePostCapsConfiguratorDataSamplerOptionFragment, ProductPriceFragment, UUID } from '../src/model/fragments'
import { Localization, useLocalization } from '../src/hooks/useLocaleFormatter'
import { CardBox } from './CardBox'
import { Picture, PictureEncode } from './Picture'
import { Card, CardView } from './Card'
import { Spacer } from './Spacer'
import { Input } from './Input'
import { multiplyPrices } from '../src/utils/formatPrice'
import { ShareLink } from './ShareLink'
import { ExportButton } from './ExportButton'
import { ContactMessageDetailsContextProvider } from '../src/contexts/ContactMessageDetailsContext'
import { ContactForm } from './ContactForm'
import { WidthBound } from './WidthBound'
import { Button } from "./Button"

export type FencePostCapsConfiguratorDTO = {
	samplerId: UUID,
	quantity: number,
	width: number,
	height: number,
	depth: number,
}

export type FencePostCapsConfiguratorLayoutProps = {
	locales: Locale[],
	options: FencePostCapsConfiguratorDataSamplerOptionFragment[]
}

const minMaxRound = (num: number, min: number, max: number, step: number = 5) => {
	const rounded = Math.round(num / step) * step;
	return Math.min(Math.max(rounded, min), max);
};

const calculatePrices = (w: number, h: number, localization: Localization, pricesPerMeter : ProductPriceFragment[]) => {
	const applicable = localization.extractPrices(pricesPerMeter);
	const area = w * h / 1000000.0;

	return multiplyPrices(applicable, area);
}

////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

const FencePostCapsConfiguratorSelection : React.FunctionComponent<{ 
	formData: FencePostCapsConfiguratorDTO,
	setFormData: (data: FencePostCapsConfiguratorDTO) => void,
	options: FencePostCapsConfiguratorDataSamplerOptionFragment[],
	locales: Locale[]
}> = ({ formData, setFormData, options, locales }) => {
	const localization = useLocalization();
	const widthRef = React.useRef<HTMLInputElement & HTMLTextAreaElement>(null) as MutableRefObject<HTMLInputElement & HTMLTextAreaElement>;
	const depthRef = React.useRef<HTMLInputElement & HTMLTextAreaElement>(null) as MutableRefObject<HTMLInputElement & HTMLTextAreaElement>;
	const quantityRef = React.useRef<HTMLInputElement & HTMLTextAreaElement>(null) as MutableRefObject<HTMLInputElement & HTMLTextAreaElement>;

	const option = React.useMemo(() => options.find(ss => ss.sampler.id == formData.samplerId), [formData.samplerId]);
	React.useEffect(() => {
		if(!option || !widthRef.current || !depthRef.current || !quantityRef.current)
		{
			return;
		}

		setFormData({
			...formData,
			width: minMaxRound(formData.width, option.minWidth, option.maxWidth, 5),
			depth: minMaxRound(formData.depth, option.minDepth, option.maxDepth, 5),
			height: option.heights.find(hh => hh.height == formData.height) ? formData.height : option.heights[0].height
		});

		widthRef.current.value = formData.width.toString();
		depthRef.current.value = formData.depth.toString();
		quantityRef.current.value = formData.quantity.toString();

	}, [option, widthRef, depthRef, quantityRef, formData.quantity, formData.samplerId, setFormData]);

	const onBlur = React.useCallback(() => {
		if(!option)
		{
			return;
		}

		const w = parseInt(widthRef.current.value);
		const d = parseInt(depthRef.current.value);
		const q = parseInt(quantityRef.current.value);
		if(isNaN(w) || isNaN(d) || isNaN(q))
		{
			return;
		}

		const data = {
			...formData, 
			width: minMaxRound(w, option.minWidth, option.maxWidth, 5),
			depth: minMaxRound(d, option.minDepth, option.maxDepth, 5),
			quantity: minMaxRound(q, 0, 9999, 1)
		};

		widthRef.current.classList.remove('input-pending');
		depthRef.current.classList.remove('input-pending');
		quantityRef.current.classList.remove('input-pending');

		setFormData(data);
		widthRef.current.value = data.width.toString();
		depthRef.current.value = data.depth.toString();
		quantityRef.current.value = data.quantity.toString();
	}, [option, widthRef, depthRef, quantityRef, formData.quantity, formData.samplerId]);

	const timeoutRef = React.useRef<NodeJS.Timeout>(null) as MutableRefObject<NodeJS.Timeout>;
	const onChange = (e : ChangeEvent) => {
		e.target.classList.add('input-pending');

		if(timeoutRef.current)
		{
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			onBlur();
		}, 1200);
	};

	return <div className='fencePostCapsConfiguratorLayout-inner'>
		<div className="fencePostCapsConfiguratorLayout-inner-selection">
			<div>
				<div>
					<h3><span className='circle'>H</span> { localization.translate('fencePostCapsConfiguratorLayout-formHeader') }:</h3>
					<div className="fencePostCapsConfiguratorLayout-inner-options">
					{
						options.map(opt => opt.heights.map(hh => {
							const data = getLocale(opt.sampler.locales, locales);
							const prices = calculatePrices(opt.minWidth, opt.minDepth, localization, hh.prices);
							
							return data && <CardBox 
								isActive={ formData.samplerId == opt.sampler.id && formData.height == hh.height }
								onClick={() => {
									setFormData({
										...formData,
										samplerId: opt.sampler.id,
										height: hh.height,
									})
								}}
								title={''}
								categoryTag={''}
								view={CardView.default}
								content={<>
										<div>
										<Picture
											image={opt.sampler.image}
											width={50}
											height={50}
											alt={data.name}
											mode="cover"
											encode={PictureEncode.jpg}
										/>
										</div>
										<div>
											<h4>{data.name}, { localization.translate('filter-height').toLocaleLowerCase() } <strong>{hh.height}mm</strong></h4>
											<p>{ localization.translate('fencePostCapsConfiguratorLayout-priceFrom') }: <span className='text-primary'>{prices.map(pp => localization.formatPrice(pp, undefined, true)).join(',')}</span> </p>
										</div>
								</>}
							/>;
						})) 
					}
					</div>
				</div>
				<Spacer initial={30} />

				<div className="fencePostCapsConfiguratorLayout-inner-inputs">
				{
					option && <div>
						<h4><span className='circle'>X</span> { localization.translate('fencePostCapsConfiguratorLayout-widthLabel') }:</h4>
						<Input
								ref={widthRef}
								defaultValue={formData.width}
								label={ localization.translate('fencePostCapsConfiguratorLayout-sizeRange') + ' ' + option.minWidth + 'mm - ' + option.maxWidth + 'mm' }
								min={option.minWidth ?? 0}
								max={option.maxWidth ?? 10000}
								type={'number'}
								onChange={onChange}
								onBlur={onBlur}
						/>
						<Spacer initial={10} />
					</div>
				}

				{
					option && <div>
						<h4><span className='circle'>Y</span> { localization.translate('fencePostCapsConfiguratorLayout-depthLabel') }:</h4>
						<Input
								ref={depthRef}
								label={ localization.translate('fencePostCapsConfiguratorLayout-sizeRange') + ' ' + option.minDepth + 'mm - ' + option.maxDepth + 'mm' }
								defaultValue={formData.depth}
								min={option.minDepth ?? 0}
								max={option.maxDepth ?? 10000}
								type={'number'}
								onChange={onChange}
								onBlur={onBlur}
						/>
						<Spacer initial={10} />
					</div>
				}
				{
					option && <div>
						<h4>{ localization.translate('products-items') }:</h4>
						<Input
								ref={quantityRef}
								label={ localization.translate('fencePostCapsConfiguratorLayout-itemCountNote') }
								defaultValue={formData.quantity}
								min={1}
								max={option.maxDepth ?? 9999}
								type={'number'}
								onChange={onChange}
								onBlur={onBlur}
						/>
					</div>
				}
				</div>

				<p>{ localization.translate('fencePostCapsConfiguratorLayout-sizeNote') }</p>
			</div>
			<div>
				<FencePostCapsSVGComponent />
			</div>
		</div>
	</div>
}


////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

const FencePostCapsConfiguratorFinal : React.FunctionComponent<{ 
	formData: FencePostCapsConfiguratorDTO,
	options: FencePostCapsConfiguratorDataSamplerOptionFragment[],
	locales: Locale[]
}> = ({ formData, options, locales }) => {
	const localization = useLocalization();
	const option = React.useMemo(() => options.find(ss => ss.sampler.id == formData.samplerId), [formData.samplerId]);
	const height = React.useMemo(() => option ? option.heights.find(hh => hh.height == formData.height) : null, [formData.height, option]);
	if(!option || !height)
	{
		return <></>;
	}

	const link = React.useMemo(() => {
		if(!globalThis.window )
		{
			return null;
		}

		const lnk = window.location.toString().replace(/\?.*$/g, '') + '?share=' + 
		encodeURIComponent(Buffer.from(JSON.stringify({fencePostCaps: formData}), 'utf8').toString('base64')) + '#tab-konfigurator';

		return lnk;
	}, [formData]);

	const samplerLocale = getLocale(option.sampler.locales, locales);
	const itemPrice = calculatePrices(formData.width, formData.depth, localization, height.prices);
	const totalPrice = multiplyPrices(itemPrice, formData.quantity);

	const gridRef = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
	return <div className='fencePostCapsConfiguratorLayout-final'>
		<h3>{ localization.translate('fencePostCapsConfiguratorLayout-projectSummary') }:</h3>
		<div className='fencePostCapsConfiguratorLayout-export-option'>
		{
			option && height && 
			[option].map(opt => [height].map(hh => {
				const data = getLocale(opt.sampler.locales, locales);
				const prices = calculatePrices(opt.minWidth, opt.minDepth, localization, hh.prices);
				
				return data && <CardBox 
					isActive={ formData.samplerId == opt.sampler.id && formData.height == hh.height }
					title={''}
					categoryTag={''}
					view={CardView.default}
					content={<>
							<div>
								<img src={opt.sampler.image.previewUrl} width="50" height="50" style={{marginRight: '10px', marginBottom: '10px'}}/>	
							</div>
							<div>
								<h4>{data.name}, { localization.translate('filter-height').toLocaleLowerCase() } <strong>{hh.height}mm</strong></h4>
								<p>{ localization.translate('fencePostCapsConfiguratorLayout-priceFrom') }: <span className='text-primary'>{prices.map(pp => localization.formatPrice(pp, undefined, true)).join(',')}</span> </p>
							</div>
					</>}
				/>;
			})) 
		}
		</div>
		<div className="grid--price" ref={gridRef}>
			<table>
				<thead>
					<tr>
						<th></th>
						<th className="text-primary"></th>
						<th className="text-primary">{ localization.translate('noVAT') }</th>
						<th className="text-primary">{ localization.translate('inclVAT') }</th>
					</tr>
				</thead>
				<tbody>
					{
						<tr className='main'>
							<th colSpan={2}>
								{ localization.translate('fencePostCapsConfiguratorLayout-postCap') } {formData.width}&nbsp;x&nbsp;{formData.depth}&nbsp;x&nbsp;{formData.height}&nbsp;mm, { samplerLocale?.name }
							</th>
							<td>{ localization.formatPricesArray(itemPrice, undefined, false).join(', ') }</td>
							<td>{ localization.formatPricesArray(itemPrice, 1, true).join(', ') }</td>
						</tr>
					}
				</tbody>
				<tfoot>
					{ totalPrice && 
						<tr>
							<th><strong>{ localization.translate('fencePostCapsConfiguratorLayout-total') }: </strong></th>
							<td><strong>{ formData.quantity }ks</strong></td>
							<td><strong>{ localization.formatPricesArray(totalPrice, undefined, false).join(', ') }</strong></td>
							<td><strong>{ localization.formatPricesArray(totalPrice, 1, true).join(', ') }</strong></td>
						</tr>
					}
				</tfoot>
			</table>
		</div>
		{
			link &&
			<div className='grid--share'>
				{
					link &&
					<ShareLink link={link} />
				}
				<ExportButton
						filename='presbeton_zakrytove_desky' 
						landscape={true} 
						documentClassName='fencePostCapsConfiguratorLayout-export'
				>{ localization.translate('print') }</ExportButton>
			</div>
		}

		<div className='fencePostCapsConfiguratorLayout-inquiry'>
			<Spacer initial={40} />
			<Card>
				<WidthBound size='narrow'>
					<h3>{ localization.translate('fencePostCapsConfiguratorLayout-projectInquiry') }</h3>
					<ContactMessageDetailsContextProvider type="configurator" subject="PRESBETON - Konfigurátor zákrytových desek" emailContentCallback={() => {

						const header = ReactDOMServer.renderToString(<>
							<h2>{ localization.translate('fencePostCapsConfiguratorLayout-projectInquiryMailHeader') }</h2>

							<div>
								<img src={option.sampler.image.previewUrl} width="50" height="50" style={{display: 'inline-block', marginRight: '10px', marginBottom: '10px'}}/>	
								{ getLocale(option.sampler.locales, locales)?.name }						
							</div>
						</>);

						return header + gridRef.current.innerHTML;
					}}>
							<ContactForm
								note={
									localization.translate('fencePostCapsConfiguratorLayout-contactForm-note')
								}
								requireCity={true}
								requireFormFields={true}
							/>
					</ContactMessageDetailsContextProvider>
				</WidthBound>
				<Spacer initial={40} />
			</Card>
		</div>

	</div>
}

////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

export const FencePostCapsConfiguratorLayout: React.FunctionComponent<FencePostCapsConfiguratorLayoutProps> =
	({ locales, options }) => {

	const localization = useLocalization();
	const [data, setData] = React.useState<FencePostCapsConfiguratorDTO | null>(null);

	React.useEffect(() => {

		const option = options[0];
		if(!option)
		{
			return;
		}

		let data = {
			samplerId: option.sampler.id,
			quantity: 1,
			width: option.minWidth,
			depth: option.minDepth,
			height: option.heights[0].height
		}

		if(globalThis.window)
		{
			const param = Object.fromEntries(window.location.search.replace('?', '').split('&').map(str => str.split('=')));
			if(param['share'] !== undefined)
			{
				const buffer = Buffer.from(decodeURIComponent(param['share']), 'base64');
				const json = JSON.parse(buffer.toString('utf8'));
				if(json && json['fencePostCaps'])
				{
					const dto = json['fencePostCaps'] as FencePostCapsConfiguratorDTO;
					data = {
						...data, ...dto
					}
				}
			}
		}
		
		setData(data);
	}, []);

	const title = localization.translate('fencePostCapsConfiguratorLayout-title');
	return (
		<div id="konfigurator" className="fencePostCapsConfiguratorLayout">
			<header>
				{
					title && <h2>{ title }</h2>
				}
				<div className='fencePostCapsConfiguratorLayout-perex' dangerouslySetInnerHTML={{ __html: localization.translate('fencePostCapsConfiguratorLayout-perex') }} />
			</header>

			{
				data && <FencePostCapsConfiguratorSelection 
					options={options}
					formData={data}
					setFormData={setData}
					locales={locales}
				/>
			}
			<Spacer initial={50} />

			{
				data?.samplerId && <FencePostCapsConfiguratorFinal
					options={options}
					formData={data}
					locales={locales}
				/>
			}
			<Spacer initial={100} />
			
		</div>
		
	);
}


const FencePostCapsSVGComponent: React.FunctionComponent<{}> = ({}) => 
{
	return <>
		<svg id="Vrstva_1" data-name="Vrstva 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 338 244.91">
		  <polyline points="83.65 113.4 74.95 101.53 288.14 92.53 296.83 104.4" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polyline points="300.42 97.16 288.14 92.53 290.14 66.82 302.42 71.45" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polyline points="23.54 29.77 35.75 25.67 74.95 101.53 62.75 107.61" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <circle fill="#299B49" r="12" cx="30.5" cy="97" />
		  <circle fill="#299B49" r="12" cx="320" cy="80" />
		  <circle fill="#299B49" r="12" cx="190" cy="128" />
		  <text transform="translate(317.55 86.03)" style={{ fontFamily: "DIN-Condensed-Bold", fill: 'white', fontSize: "14.47px" }}><tspan x="0" y="0">H</tspan></text>
		  <text transform="translate(187.56 133.53)" style={{ fontFamily: "DIN-Condensed-Bold", fill: 'white', fontSize: "14.47px" }}><tspan x="0" y="0">X</tspan></text>
		  <text transform="translate(27.87 101.97)" style={{ fontFamily: "DIN-Condensed-Bold", fill: 'white', fontSize: "14.47px", letterSpacing: "-.02em" }}><tspan x="0" y="0">Y</tspan></text>
		  
		  <g>
			<line x1="89.49" y1="110.74" x2="287.45" y2="102.31" style={{ fill: "none", stroke: "#000", strokeMiterlimit: 10, strokeWidth: ".75px" }} />
			<polygon points="91.07 112.86 82.85 111.02 90.89 108.5 91.07 112.86" />
			<polygon points="286.06 104.55 294.09 102.03 285.87 100.19 286.06 104.55" />
		  </g>
		  <g>
			<line x1="29.85" y1="35.11" x2="62.73" y2="99.69" style={{ fill: "none", stroke: "#000", strokeMiterlimit: 10, strokeWidth: ".75px" }} />
			<polygon points="28.58 37.42 26.84 29.19 32.47 35.44 28.58 37.42" />
			<polygon points="60.12 99.36 65.75 105.61 64 97.38 60.12 99.36" />
		  </g>
		  <g>
			<line x1="299.62" y1="90.09" x2="300.37" y2="78" style={{ fill: "none", stroke: "#000", strokeMiterlimit: 10, strokeWidth: ".75px" }} />
			<polygon points="301.89 88.74 299.21 96.72 297.54 88.47 301.89 88.74" />
			<polygon points="302.45 79.61 300.78 71.36 298.1 79.34 302.45 79.61" />
		  </g>
		  <polygon points="290.14 66.82 74.95 72.82 34.96 3.86 198.64 .5 290.14 66.82" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="35.96 25.87 74.95 101.53 74.95 72.82 34.96 3.86 35.96 25.87" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="74.95 101.53 288.14 92.53 290.14 66.82 74.95 72.82 74.95 101.53" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="74.95 100.53 288.14 91.53 290.14 68.82 74.95 74.82 74.95 100.53" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <path d="M297.12,183.56l1.68,1.68v24.46s-1.68,1.68-1.68,1.68h-8.33s-.08-.04-.08-.04c0-2.13-1.73-3.86-3.86-3.86s-3.86,1.73-3.86,3.86l.08.04H46.76s-.08-.04-.08-.04c0-2.13-1.73-3.86-3.86-3.86s-3.86,1.73-3.86,3.86v.04h-8.33s-1.61-1.68-1.61-1.68v-24.46s1.61-1.68,1.61-1.68h266.48s-8.33,0-8.33,0" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <line x1="285.09" y1="215.13" x2="285.09" y2="244.41" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <line x1="299.56" y1="212.49" x2="299.56" y2="244.41" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <g>
			<polyline points="278.7 235.77 266.61 235.77 299.56 235.77" style={{ fill: "none", stroke: "#000", strokeMiterlimit: 10, strokeWidth: ".75px" }} />
			<polygon points="277.21 233.59 285.34 235.77 277.21 237.95 277.21 233.59" />
		  </g>
		  <g>
			<polyline points="306.2 235.77 338 235.77 285.34 235.77" style={{ fill: "none", stroke: "#000", strokeMiterlimit: 10, strokeWidth: ".75px" }} />
			<polygon points="307.69 237.95 299.56 235.77 307.69 233.59 307.69 237.95" />
		  </g>
		  <text transform="translate(312.05 217.86)" style={{ fontFamily: "Helvetica, Helvetica", fontSize: "12px" }}><tspan x="0" y="0">cca</tspan></text>
		  <text transform="translate(304.85 228.5)" style={{ fontFamily: "Helvetica, Helvetica", fontSize: "12px" }}><tspan x="0" y="0">25mm</tspan></text>
		  <text transform="translate(0 161.54)" style={{ fontFamily: "ArialMT, Arial", fontSize: "12px" }}><tspan x="0" y="0">fazeta 9 nebo 13 mm</tspan></text>
		  <line x1="28.46" y1="182.93" x2="12.6" y2="167.07" style={{ fill: "none", stroke: "#575756", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="34.96 24.87 73.95 100.53 73.98 74.4 34.41 5.73 34.96 24.87" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="74.95 72.82 73.98 74.4 73.95 100.53 74.95 101.53 75.86 100.69 75.8 74.78 74.95 72.82" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <line x1="73.98" y1="74.4" x2="75.8" y2="74.78" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		  <polygon points="34.96 3.86 34.41 5.73 73.96 74.27 74.95 72.82 34.96 3.86" style={{ fill: "#e1e1e1", stroke: "#000", strokeLinecap: "round", strokeLinejoin: "round" }} />
		</svg>
	</>
}